import React, { useMemo } from "react";
import { useTheme } from "next-themes";

/**
 * Global, subtle animated background for the whole site.
 * Fixed full-screen with floating particles + slow light blobs.
 * Sits at z-0 behind all content. Uses CSS animations already defined in index.css.
 */
export default function SiteBackground() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const particles = useMemo(
    () =>
      Array.from({ length: 36 }).map((_, i) => {
        const size = 1 + Math.random() * 2.6;
        const left = Math.random() * 100;
        const delay = Math.random() * -40;
        const duration = 28 + Math.random() * 30;
        const opacity = 0.15 + Math.random() * 0.45;
        return { i, size, left, delay, duration, opacity };
      }),
    []
  );

  const accent = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
  const red = "rgba(225,6,0,0.07)";

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 0 }}
      data-testid="site-bg"
    >
      {/* Soft moving blobs */}
      <div
        className="hero-blob-a absolute -top-1/4 -left-1/4 w-[60vw] h-[60vw] rounded-full"
        style={{ background: `radial-gradient(closest-side, ${accent}, transparent 70%)`, filter: "blur(60px)" }}
      />
      <div
        className="hero-blob-b absolute top-1/3 -right-1/4 w-[55vw] h-[55vw] rounded-full"
        style={{ background: `radial-gradient(closest-side, ${red}, transparent 70%)`, filter: "blur(80px)" }}
      />
      <div
        className="hero-blob-c absolute -bottom-1/3 left-1/4 w-[55vw] h-[55vw] rounded-full"
        style={{ background: `radial-gradient(closest-side, ${accent}, transparent 70%)`, filter: "blur(70px)" }}
      />

      {/* Floating particles */}
      {particles.map((p) => (
        <span
          key={p.i}
          className="particle bg-white"
          style={{
            left: `${p.left}%`,
            bottom: "-12px",
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            opacity: isDark ? p.opacity : p.opacity * 0.5,
            filter: "blur(0.5px)",
          }}
        />
      ))}
    </div>
  );
}
